import { Box, Button, IconButton, Modal, SxProps } from "@mui/material"
import { ReactNode, createContext, memo, useEffect, useState } from "react"
import styles from "./NotesModule.module.css"
import { NotesContext, TNotesModule, TTabList } from "./NotesModuleContext"
import AddNotes from "./AddNotes/AddNotes"
import PreviousNotes from "./PreviousNotes/PreviousNotes"
import BookingNotes from "./BookingNotes/BookingNotes"
import CloseIcon from "@mui/icons-material/Close"
import noResults from "../../utils/assets/images/No Results.svg"
import { EventLogger } from "@/utils/CommonFunctions"

const ModalSX: SxProps = {
  display: "flex",
  justifyContent: "center",
  alignContent: "center",
}

const NoModuleProps = ({ data, error, children }: { data?: any; error: string; children: ReactNode }) => {
  return (
    <>
      {data ? (
        <>{children}</>
      ) : (
        <Box className={styles.NoDataFound}>
          <img src={noResults.src} alt="empty-cart" width="150px" height="150px" />
          <Box>{error ?? "No Data Found"}</Box>
        </Box>
      )}
    </>
  )
}

const NotesModule = (props: TNotesModule) => {
  const { open, onClose, moduleName, addNotes, previousNotes, bookingNotes } = props

  const [currentView, setCurrentView] = useState<TTabList>("")

  useEffect(() => {
    setCurrentView(addNotes ? "add_notes" : previousNotes ? "prev_notes" : bookingNotes ? "booking_notes" : "")
  }, [addNotes, bookingNotes, open, previousNotes])

  const onClickTabItem = (tab: TTabList) => {
    setCurrentView(tab)
    if (moduleName) {
      if (tab === "add_notes") EventLogger("admin_addnotes")
      else if (tab === "booking_notes") EventLogger("admin_bookingnotes")
      else if (tab === "prev_notes") EventLogger("admin_noteslog")
    }
  }

  const onCloseModal = () => {
    if (typeof onClose === "function") onClose()
  }

  return (
    <NotesContext.Provider value={{ ...props }}>
      <Modal open={open} onClose={onCloseModal} sx={ModalSX}>
        <Box className={styles.NotesSection}>
          <Box className={styles.NotesContainer}>
            <Box className={styles.TabHeader}>
              <Box className={styles.TabList}>
                <>
                  <Button
                    className={`${styles.TabItem} ${currentView === "add_notes" ? "Selected" : ""}`}
                    onClick={() => onClickTabItem("add_notes")}
                    disabled={addNotes ? false : true}
                  >
                    Add Notes
                  </Button>
                  <Button
                    className={`${styles.TabItem} ${currentView === "prev_notes" ? "Selected" : ""}`}
                    onClick={() => onClickTabItem("prev_notes")}
                    disabled={previousNotes ? false : true}
                  >
                    Notes Log
                  </Button>
                  <Button
                    className={`${styles.TabItem} ${currentView === "booking_notes" ? "Selected" : ""}`}
                    onClick={() => onClickTabItem("booking_notes")}
                    disabled={bookingNotes ? false : true}
                  >
                    Booking Notes
                  </Button>
                </>
              </Box>
              <IconButton className={styles.CloseIcon} onClick={onCloseModal}>
                <CloseIcon sx={{ color: "white" }} />
              </IconButton>
            </Box>

            <Box className={styles.TabContent}>
              {currentView === "add_notes" && (
                <>
                  <NoModuleProps data={addNotes} error={"Adding Notes Unavailable"}>
                    <AddNotes />
                  </NoModuleProps>
                </>
              )}
              {currentView === "prev_notes" && (
                <NoModuleProps data={previousNotes} error={"Notes Log Unavailable"}>
                  <PreviousNotes />
                </NoModuleProps>
              )}
              {currentView === "booking_notes" && (
                <NoModuleProps data={bookingNotes?.reservationId} error={"Booking Notes Unavailable"}>
                  <BookingNotes />
                </NoModuleProps>
              )}
            </Box>
          </Box>
        </Box>
      </Modal>
    </NotesContext.Provider>
  )
}

export default memo(NotesModule)
